import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay } from 'date-fns';

export type PaymentStatusType = 'up_to_date' | 'overdue' | 'partial' | 'settled' | 'no_payments';

export interface CasePaymentStatus {
  status: PaymentStatusType;
  overdueCount: number;
  overdueAmount: number;
  totalPending: number;
  maxDaysOverdue: number;
}

export interface PaymentForStatus {
  status: string | null;
  amount: number | null;
  paid_amount: number | null;
  due_date: string | null;
}

const EMPTY_STATUS: CasePaymentStatus = {
  status: 'no_payments',
  overdueCount: 0,
  overdueAmount: 0,
  totalPending: 0,
  maxDaysOverdue: 0,
};

/**
 * Pure computation of a deal/case payment status from its payments.
 * Shared by useCasePaymentStatus and the processos report so the
 * "em dia vs atrasado" rule stays identical everywhere.
 */
export function computePaymentStatus(
  payments: PaymentForStatus[] | null | undefined,
  today: Date = startOfDay(new Date())
): CasePaymentStatus {
  if (!payments || payments.length === 0) {
    return { ...EMPTY_STATUS };
  }

  // Separate payments by status and due date
  const pendingPayments = payments.filter(p => p.status !== 'paid' && p.status !== 'cancelled');
  const overduePayments = pendingPayments.filter(p => {
    if (!p.due_date) return true; // No due date = consider overdue
    return new Date(p.due_date) < today;
  });

  const allPaid = payments.every(p => p.status === 'paid');
  const allPaidOrCancelled = payments.every(p => p.status === 'paid' || p.status === 'cancelled');

  // Calculate amounts
  const overdueAmount = overduePayments.reduce((sum, p) => sum + ((p.amount || 0) - (p.paid_amount || 0)), 0);
  const totalPending = pendingPayments.reduce((sum, p) => sum + ((p.amount || 0) - (p.paid_amount || 0)), 0);

  // Calculate max days overdue
  const maxDaysOverdue = overduePayments.reduce((maxDays, p) => {
    if (!p.due_date) return maxDays;
    const dueDate = startOfDay(new Date(p.due_date));
    const diffTime = today.getTime() - dueDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(maxDays, diffDays);
  }, 0);

  let status: PaymentStatusType;

  if (allPaid) {
    status = 'settled';
  } else if (allPaidOrCancelled) {
    status = 'settled';
  } else if (overduePayments.length === 0) {
    // No overdue payments - client is up to date
    status = 'up_to_date';
  } else if (overduePayments.length < pendingPayments.length) {
    // Some overdue but not all
    status = 'partial';
  } else {
    status = 'overdue';
  }

  return {
    status,
    overdueCount: overduePayments.length,
    overdueAmount,
    totalPending,
    maxDaysOverdue,
  };
}

export function useCasePaymentStatus(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['case-payment-status', dealId],
    queryFn: async (): Promise<CasePaymentStatus> => {
      if (!dealId) {
        return { ...EMPTY_STATUS };
      }

      const { data: payments, error } = await supabase
        .from('payments')
        .select('id, status, amount, paid_amount, due_date')
        .eq('deal_id', dealId);

      if (error) throw error;

      return computePaymentStatus(payments);
    },
    enabled: !!dealId,
    staleTime: 60000, // 1 minute
  });
}
